import React, { useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";

// Define a type for window with Calendly
declare global {
  interface Window {
    Calendly?: any;
  }
}

const calendlyUrl = "https://calendly.com/kontakt-pianoundervisning/30min?primary_color=d4af37";

const FreeTrialBooking = () => {
  const calendlyRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Create a function to load Calendly script
    const loadCalendlyScript = () => {
      // Only inject Calendly script if not already present
      if (!document.getElementById("calendly-widget-script") && !window.Calendly) {
        const script = document.createElement("script");
        script.id = "calendly-widget-script";
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        script.onload = () => setIsCalendlyLoaded(true);
        document.body.appendChild(script);
        
        // Preconnect to Calendly domain for faster loading
        if (!document.querySelector('link[rel="preconnect"][href="https://calendly.com"]')) {
          const preconnectLink = document.createElement("link");
          preconnectLink.rel = "preconnect";
          preconnectLink.href = "https://calendly.com";
          document.head.appendChild(preconnectLink);
        }
      } else {
        setIsCalendlyLoaded(true);
      }
    };
    
    loadCalendlyScript();
    
    // Add SEO structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Gratis prøvetime i klaverundervisning",
      "description": "Book en gratis og uforpligtende prøvetime i klaverundervisning",
      "provider": {
        "@type": "Person",
        "name": "Oscar Gilbert",
        "description": "Konservatorieuddannet pianist"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "DKK"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Clean up script when component unmounts
      document.head.removeChild(script);
    };
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Book gratis prøvetime i klaverundervisning | Oscar Gilbert</title>
        <meta name="description" content="Book en gratis og uforpligtende prøvetime i klaverundervisning. Vælg en tid direkte i kalenderen." />
        <link rel="canonical" href="https://pianoundervisning.dk/gratis-proevetime" />
        <meta property="og:title" content="Book gratis prøvetime i klaverundervisning | Oscar Gilbert" />
        <meta property="og:description" content="Book en gratis og uforpligtende prøvetime i klaverundervisning. Vælg en tid direkte i kalenderen." />
        <meta property="og:url" content="https://pianoundervisning.dk/gratis-proevetime" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <section
        className="min-h-[80vh] flex justify-center items-start py-10 px-2"
        style={{
          background: isMobile
            ? undefined
            : "linear-gradient(135deg, #000000e6 0%, #222 100%)"
        }}
      >
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4 text-gray-900">
          <div
            ref={calendlyRef}
            className="calendly-inline-widget"
            data-url={calendlyUrl}
            style={{ minWidth: "320px", height: "700px" }}
          />
        </div>
      </section>
    </Layout>
  );
};

export default FreeTrialBooking;
